import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, TextField, IconButton, Typography, List, ListItem,
  ListItemText, Tooltip, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { IInhouseCategory } from './types';
import { fetchCategories, createCategory, updateCategory, deleteCategory, reorderCategories } from './inhouseApi';

interface InhouseCategoryManagerProps {
  open: boolean;
  onClose: () => void;
  onCategoriesChanged: () => void;
}

const PRESET_COLORS = [
  '#2196F3', '#E91E63', '#FF9800', '#9C27B0',
  '#F44336', '#009688', '#3F51B5', '#607D8B',
  '#4CAF50', '#FF5722', '#795548', '#00BCD4',
];

const InhouseCategoryManager: React.FC<InhouseCategoryManagerProps> = ({
  open,
  onClose,
  onCategoriesChanged,
}) => {
  const [categories, setCategories] = useState<IInhouseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New category form
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#2196F3');
  const [showNewForm, setShowNewForm] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editColor, setEditColor] = useState('');

  const load = async () => {
    try {
      const cats = await fetchCategories();
      setCategories(cats);
    } catch {
      setError('Kunde inte hämta kategorier');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setError(null);
    try {
      const id = newLabel.toLowerCase()
        .replace(/[åä]/g, 'a').replace(/ö/g, 'o')
        .replace(/[^a-z0-9]/g, '').trim();
      await createCategory({ id, label: newLabel.trim(), color: newColor });
      setNewLabel('');
      setShowNewForm(false);
      await load();
      onCategoriesChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Kunde inte skapa kategori');
    }
  };

  const handleUpdate = async () => {
    if (!editingId || !editLabel.trim()) return;
    setError(null);
    try {
      await updateCategory(editingId, { label: editLabel.trim(), color: editColor });
      setEditingId(null);
      await load();
      onCategoriesChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Kunde inte uppdatera kategori');
    }
  };

  const handleDelete = async (id: string) => {
    setError(null);
    try {
      await deleteCategory(id);
      await load();
      onCategoriesChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Kunde inte ta bort kategori');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const newCats = [...categories];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newCats.length) return;

    // Swap
    [newCats[index], newCats[swapIndex]] = [newCats[swapIndex], newCats[index]];

    // Optimistic update
    setCategories(newCats);

    // Save new order to backend
    try {
      const order = newCats.map((cat, i) => ({ id: cat.id, order: i }));
      await reorderCategories(order);
      onCategoriesChanged();
    } catch (err: any) {
      setError('Kunde inte ändra ordning');
      await load(); // revert
    }
  };

  const startEdit = (cat: IInhouseCategory) => {
    setEditingId(cat.id);
    setEditLabel(cat.label);
    setEditColor(cat.color);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Hantera kategorier
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <List dense>
          {categories.map((cat, index) => (
            <ListItem
              key={cat.id}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
              secondaryAction={
                editingId === cat.id ? (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={handleUpdate}><SaveIcon sx={{ fontSize: 18 }} /></IconButton>
                    <IconButton size="small" onClick={() => setEditingId(null)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.3 }}>
                    <Tooltip title="Flytta upp">
                      <span>
                        <IconButton size="small" onClick={() => handleMove(index, 'up')} disabled={index === 0}>
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Flytta ner">
                      <span>
                        <IconButton size="small" onClick={() => handleMove(index, 'down')} disabled={index === categories.length - 1}>
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <IconButton size="small" onClick={() => startEdit(cat)}><EditIcon sx={{ fontSize: 18 }} /></IconButton>
                    <IconButton size="small" onClick={() => handleDelete(cat.id)} color="error"><DeleteIcon sx={{ fontSize: 18 }} /></IconButton>
                  </Box>
                )
              }
            >
              {editingId === cat.id ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pr: 8 }}>
                  <input
                    type="color"
                    value={editColor}
                    onChange={(e) => setEditColor(e.target.value)}
                    style={{ width: 32, height: 32, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                  />
                  <TextField
                    size="small"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdate()}
                    fullWidth
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: cat.color, flexShrink: 0 }} />
                  <ListItemText
                    primary={cat.label}
                    secondary={cat.id}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </Box>
              )}
            </ListItem>
          ))}
        </List>

        {/* New category form */}
        {showNewForm ? (
          <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Ny kategori
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
              />
              <TextField
                size="small"
                label="Namn"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                fullWidth
                placeholder="t.ex. Katalog"
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
              {PRESET_COLORS.map(c => (
                <Box
                  key={c}
                  onClick={() => setNewColor(c)}
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: c,
                    cursor: 'pointer',
                    border: newColor === c ? '2px solid #000' : '2px solid transparent',
                    '&:hover': { transform: 'scale(1.2)' },
                    transition: 'transform 0.15s',
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" size="small" onClick={handleCreate} disabled={!newLabel.trim()}
                sx={{ bgcolor: '#072f53', '&:hover': { bgcolor: '#0a4175' }, textTransform: 'none' }}>
                Skapa
              </Button>
              <Button variant="outlined" size="small" onClick={() => setShowNewForm(false)}
                sx={{ textTransform: 'none' }}>
                Avbryt
              </Button>
            </Box>
          </Box>
        ) : (
          <Button
            startIcon={<AddIcon />}
            onClick={() => setShowNewForm(true)}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            Lägg till ny kategori
          </Button>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InhouseCategoryManager;
